import React, { lazy, memo, PropsWithChildren, Suspense, useEffect, useMemo, useState } from 'react'
import { isOiAnalysisView, ViewType } from '../assets/constants'
import StockBar from './home/stockBar/stockBar'
import { useStyles } from './homeLayoutStyles'
import { Button, Stack, Typography, useTheme } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { selectIsEmbedded } from '../selectors/device'
import { selectFinancialPanelOpen, selectSeasonalsPanelOpen, selectTechnicalsPanelOpen } from '../selectors/home'
import store from '../store'
import { useHomeLayoutNavigation } from './home/useHomeLayoutNavigation'
import FinancialDataSkeleton from './financialData/FinancialDataSkeleton'
import TechnicalsSkeleton from './technicals/TechnicalsSkeleton'
import SubtleShimmer from '../components/common/SubtleShimmer'
import { setFinancialPanelOpen, setSeasonalsPanelOpen, setTechnicalsPanelOpen } from '../reducer/home.reducer'

const FinancialDataContent = lazy(() => import('./financialData/FinancialDataContent'))
const SeasonalsContent = lazy(() => import('./seasonals/SeasonalsContent'))
const TechnicalsContent = lazy(() => import('./technicals/TechnicalsContent'))

export type HomeLayoutProps = PropsWithChildren & {
    view: ViewType,
    error?: string
}

const FINANCIAL_PANEL_VIEWS = new Set<ViewType>([ViewType.CHART, ViewType.SYMBOL])
const DEDICATED_SECURITY_PANEL_VIEWS = new Set<ViewType>([
    ViewType.SEASONALS,
    ViewType.TECHNICALS,
    ViewType.INDEX_COMPONENTS,
    ViewType.FINANCIAL_DATA,
])

const contentAreaStyle = (view: ViewType, background: string): React.CSSProperties =>
    view === ViewType.AUTOMATION
        ? { gridArea: 'content', overflow: 'auto', background }
        : {
              gridArea: 'content',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              height: '100%',
              background
          }

const HomeLayout: React.FC<HomeLayoutProps> = ({ view, error, children }) => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { classes, cx } = useStyles()
    const theme = useTheme()
    const financialPanelOpen = useSelector(selectFinancialPanelOpen)
    const seasonalsPanelOpen = useSelector(selectSeasonalsPanelOpen)
    const technicalsPanelOpen = useSelector(selectTechnicalsPanelOpen)
    const [isEmbedded, setIsEmbedded] = useState(() => selectIsEmbedded(store.getState()))
    const showFinancialPanel = financialPanelOpen && FINANCIAL_PANEL_VIEWS.has(view)
    // Chart overlays stay Redux-driven; dedicated /seasonals and /technicals routes render as children.
    const showSeasonalsPanel = seasonalsPanelOpen && FINANCIAL_PANEL_VIEWS.has(view)
    const showTechnicalsPanel = technicalsPanelOpen && FINANCIAL_PANEL_VIEWS.has(view)
    const stockBarView = showFinancialPanel
        ? ViewType.FINANCIAL_DATA
        : showSeasonalsPanel
          ? ViewType.SEASONALS
          : showTechnicalsPanel
            ? ViewType.TECHNICALS
            : view

    useHomeLayoutNavigation(view)

    useEffect(() => {
        if (!FINANCIAL_PANEL_VIEWS.has(view) && view !== ViewType.FINANCIAL_DATA && financialPanelOpen) {
            dispatch(setFinancialPanelOpen(false))
        }
    }, [dispatch, financialPanelOpen, view])

    useEffect(() => {
        if (!FINANCIAL_PANEL_VIEWS.has(view) && view !== ViewType.SEASONALS && seasonalsPanelOpen) {
            dispatch(setSeasonalsPanelOpen(false))
        }
    }, [dispatch, seasonalsPanelOpen, view])

    useEffect(() => {
        if (!FINANCIAL_PANEL_VIEWS.has(view) && view !== ViewType.TECHNICALS && technicalsPanelOpen) {
            dispatch(setTechnicalsPanelOpen(false))
        }
    }, [dispatch, technicalsPanelOpen, view])

    useEffect(() => {
        const sync = () => {
            const next = selectIsEmbedded(store.getState())
            setIsEmbedded((prev) => (prev === next ? prev : next))
        }
        return store.subscribe(sync)
    }, [])

    const contentStyle = useMemo(
        () =>
            contentAreaStyle(
                stockBarView,
                view === ViewType.AUTOMATION ||
                    DEDICATED_SECURITY_PANEL_VIEWS.has(view) ||
                    showFinancialPanel ||
                    showSeasonalsPanel ||
                    showTechnicalsPanel
                    ? theme.palette.background.paper
                    : theme.palette.background.default,
            ),
        [showFinancialPanel, showSeasonalsPanel, showTechnicalsPanel, stockBarView, theme.palette.background.default, theme.palette.background.paper, view],
    )

    return (
        <div className={classes.container}>
            {error ? (
                <Stack
                    className="empty-view"
                    justifyContent="center"
                    alignItems="center"
                    direction="column"
                    spacing={1}
                >
                    <Typography variant="body1">{error}</Typography>
                    <Button
                        variant="outlined"
                        color="inherit"
                        onClick={() => {
                            navigate({ pathname: '/', search: '' }, { replace: true })
                            window.location.reload()
                        }}
                    >
                        Go To Home
                    </Button>
                </Stack>
            ) : (
                <>
                    {!isEmbedded && (
                        <StockBar
                            viewType={stockBarView}
                            ivPlotTimeBucket={isOiAnalysisView(view)}
                        />
                    )}
                    <div style={contentStyle}>
                        {showFinancialPanel ? (
                            <Suspense fallback={<FinancialDataSkeleton />}>
                                <FinancialDataContent />
                            </Suspense>
                        ) : showSeasonalsPanel ? (
                            <Suspense fallback={<FinancialDataSkeleton />}>
                                <SeasonalsContent />
                            </Suspense>
                        ) : showTechnicalsPanel ? (
                            <Suspense
                                fallback={
                                    <SubtleShimmer loading fill>
                                        <TechnicalsSkeleton />
                                    </SubtleShimmer>
                                }
                            >
                                <TechnicalsContent />
                            </Suspense>
                        ) : (
                            children
                        )}
                    </div>
                </>
            )}
        </div>
    )
}

function homeLayoutPropsAreEqual(prev: HomeLayoutProps, next: HomeLayoutProps): boolean {
    if (prev.view !== next.view || prev.error !== next.error) return false
    // Chart page: children is stable <CashViewContent />; tick/layout state updates inside that subtree.
    if (prev.view === ViewType.CHART) return true
    return prev.children === next.children
}

export default memo(HomeLayout, homeLayoutPropsAreEqual)
